import { defineStore } from "pinia";

const STORAGE_KEY = "ik_cookie_consent";
const COOKIE_MAX_AGE_SECONDS = 365 * 24 * 60 * 60;

const defaultCategories = () => ({
  essential: true, // always on, can't be turned off
  analytics: false,
  functionality: false,
  targeting: false,
});

function readCookie(name) {
  if (typeof document === "undefined") return "";
  const parts = String(document.cookie || "").split("; ");
  for (const part of parts) {
    if (!part) continue;
    const [rawKey, ...rawValueParts] = part.split("=");
    if (rawKey !== name) continue;
    const value = rawValueParts.join("=") || "";
    try {
      return decodeURIComponent(value);
    } catch (error) {
      return value;
    }
  }
  return "";
}

function writeCookie(name, value) {
  if (typeof document === "undefined") return;
  const cookieValue = String(value || "");
  document.cookie = `${name}=${cookieValue}; path=/; max-age=${COOKIE_MAX_AGE_SECONDS}; samesite=lax`;
}

function readSavedConsent() {
  const fromCookie = readCookie(STORAGE_KEY);
  if (fromCookie) {
    return fromCookie;
  }

  // One-time migration from previous localStorage persistence.
  if (typeof window !== "undefined") {
    const fromLocalStorage = window.localStorage.getItem(STORAGE_KEY) || "";
    if (fromLocalStorage) {
      writeCookie(STORAGE_KEY, fromLocalStorage);
      window.localStorage.removeItem(STORAGE_KEY);
      return fromLocalStorage;
    }
  }

  return "";
}

export const useCookieConsentStore = defineStore("cookieConsent", {
  state: () => ({
    consentGiven: false, // never assume consent — must be explicitly resolved by init()
    categories: defaultCategories(),
    initialized: false, // guards against init() running its logic more than once
  }),
  getters: {
    showBanner: (state) => state.initialized && !state.consentGiven,
  },
  actions: {
    init() {
      // Prevent repeated mounts from re-running resolution and causing flicker
      if (this.initialized) return;

      const saved = readSavedConsent();

      if (!saved) {
        this.consentGiven = false;
        this.categories = defaultCategories();
        this.initialized = true;
        return;
      }

      try {
        const parsed = JSON.parse(saved);
        this.categories = {
          ...defaultCategories(),
          ...parsed.categories,
          essential: true,
        };
        this.consentGiven = true;
      } catch (e) {
        this.consentGiven = false;
        this.categories = defaultCategories();
      }

      this.initialized = true;
    },
    resync() {
      // Always re-reads the cookie fresh, bypassing the `initialized` guard.
      // Guards against reassigning state to equivalent values so polling
      // doesn't cause needless reactivity churn.
      const saved = readSavedConsent();

      if (!saved) {
        if (this.consentGiven !== false) {
          this.consentGiven = false;
        }
        if (
          JSON.stringify(this.categories) !==
          JSON.stringify(defaultCategories())
        ) {
          this.categories = defaultCategories();
        }
        return;
      }

      try {
        const parsed = JSON.parse(saved);
        const nextCategories = {
          ...defaultCategories(),
          ...parsed.categories,
          essential: true,
        };
        if (
          JSON.stringify(nextCategories) !== JSON.stringify(this.categories)
        ) {
          this.categories = nextCategories;
        }
        if (this.consentGiven !== true) {
          this.consentGiven = true;
        }
      } catch (e) {
        this.consentGiven = false;
        this.categories = defaultCategories();
      }
    },
    persist() {
      writeCookie(
        STORAGE_KEY,
        JSON.stringify({ categories: this.categories, ts: Date.now() }),
      );
      this.consentGiven = true;
    },
    acceptAll() {
      this.categories = {
        essential: true,
        analytics: true,
        functionality: true,
        targeting: true,
      };
      this.persist();
    },
    acceptNecessaryOnly() {
      this.categories = defaultCategories();
      this.persist();
    },
    setCategory(key, value) {
      if (key === "essential") return;
      this.categories[key] = value;
    },
    savePreferences() {
      this.persist();
    },
  },
});
